import { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import { Plus, X, ChevronLeft, ChevronRight, Edit2, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const DAYS = ['Mo','Di','Mi','Do','Fr','Sa','So'];
const MONTHS = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];
const EVENT_TYPES = ['allgemein','tuev','inspektion','veranstaltung','sonstiges'];
const COLORS = ['#3b82f6','#e63946','#16a34a','#d97706','#7c3aed','#0891b2','#db2777'];

const EMPTY = {
  title:'', event_type:'allgemein', vehicle_id:'',
  start_date: new Date().toISOString().split('T')[0],
  end_date: new Date().toISOString().split('T')[0],
  description:'', color:'#3b82f6', all_day:true
};

export default function Kalender() {
  const [events, setEvents]   = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [year, setYear]       = useState(new Date().getFullYear());
  const [month, setMonth]     = useState(new Date().getMonth());
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(() => {
    const start = `${year}-${String(month+1).padStart(2,'0')}-01`;
    const d = new Date(year, month+1, 0);
    const end = `${year}-${String(month+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
    api.get('/calendar', { params: { start, end } }).then(r => setEvents(r.data)).catch(() => {});
  }, [year, month]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get('/vehicles').then(r => setVehicles(r.data)).catch(() => {}); }, []);

  const prevMonth = () => { if (month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
  const nextMonth = () => { if (month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

  const openCreate = (date) => {
    const d = date || new Date().toISOString().split('T')[0];
    setEditing(null);
    setForm({ ...EMPTY, start_date:d, end_date:d });
    setSelected(null);
    setModal(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title, event_type: ev.event_type||'allgemein',
      vehicle_id: ev.vehicle_id||'',
      start_date: ev.start_date?.split('T')[0]||'',
      end_date: ev.end_date?.split('T')[0]||ev.start_date?.split('T')[0]||'',
      description: ev.description||'',
      color: ev.color||'#3b82f6', all_day: ev.all_day!==0
    });
    setSelected(null);
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await api.put(`/calendar/${editing.id}`, form); toast.success('Termin aktualisiert'); }
      else { await api.post('/calendar', form); toast.success('Termin erstellt'); }
      setModal(false); load();
    } catch(err) { toast.error(err.response?.data?.error||'Fehler'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Termin löschen?')) return;
    try { await api.delete(`/calendar/${id}`); toast.success('Gelöscht'); load(); setSelected(null); }
    catch { toast.error('Fehler'); }
  };

  const f = (k,v) => setForm(p => ({ ...p, [k]:v }));

  // Kalender Daten berechnen
  const firstDay = new Date(year, month, 1);
  let startDow = firstDay.getDay(); // 0=So
  startDow = startDow === 0 ? 6 : startDow - 1; // Mo=0
  const daysInMonth = new Date(year, month+1, 0).getDate();
  const cells = [];
  for (let i=0; i<startDow; i++) cells.push(null);
  for (let d=1; d<=daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const eventsForDay = (d) => {
    if (!d) return [];
    const dateStr = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    return events.filter(e => e.start_date?.split('T')[0] <= dateStr && (e.end_date?.split('T')[0]||e.start_date?.split('T')[0]) >= dateStr);
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = (d) => d && `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}` === todayStr;

  return (
    <Layout title="Kalender">
      {/* Monats-Navigation */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16, flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button className="btn btn-ghost btn-icon" onClick={prevMonth}><ChevronLeft size={16}/></button>
          <h2 style={{ fontSize:'1rem', fontWeight:700, minWidth:140, textAlign:'center' }}>
            {MONTHS[month]} {year}
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={nextMonth}><ChevronRight size={16}/></button>
          <button className="btn btn-secondary btn-sm" onClick={() => { setMonth(new Date().getMonth()); setYear(new Date().getFullYear()); }}>
            Heute
          </button>
        </div>
        <button className="btn btn-primary" onClick={() => openCreate()}>
          <Plus size={14}/> Neuer Termin
        </button>
      </div>

      {/* Kalender-Grid */}
      <div className="card">
        {/* Wochentage */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', borderBottom:'2px solid var(--border)' }}>
          {DAYS.map(d => (
            <div key={d} style={{ padding:'8px 4px', textAlign:'center', fontSize:'.7rem', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'.05em' }}>
              {d}
            </div>
          ))}
        </div>

        {/* Tage */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)' }}>
          {cells.map((d, i) => {
            const dayEvents = eventsForDay(d);
            const today = isToday(d);
            return (
              <div key={i}
                onClick={() => d && openCreate(`${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`)}
                style={{
                  minHeight: 80, padding:'6px 8px',
                  borderRight: (i+1)%7===0 ? 'none' : '1px solid var(--border)',
                  borderBottom: i < cells.length-7 ? '1px solid var(--border)' : 'none',
                  background: d ? 'transparent' : 'var(--bg)',
                  cursor: d ? 'pointer' : 'default',
                  transition: 'background 120ms',
                }}
                onMouseEnter={e => d && (e.currentTarget.style.background='var(--bg)')}
                onMouseLeave={e => d && (e.currentTarget.style.background='transparent')}>
                {d && (
                  <>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%',
                      background: today ? 'var(--accent)' : 'transparent',
                      color: today ? '#fff' : 'var(--text)',
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:'.78rem', fontWeight: today ? 700 : 500,
                      marginBottom: 4,
                    }}>{d}</div>
                    <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
                      {dayEvents.slice(0,3).map(ev => (
                        <div key={ev.id}
                          onClick={e => { e.stopPropagation(); setSelected(selected?.id===ev.id ? null : ev); }}
                          style={{
                            background: ev.color||'#3b82f6', color:'#fff',
                            borderRadius:4, padding:'1px 5px', fontSize:'.62rem',
                            fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                            cursor:'pointer',
                          }}>
                          {ev.title}
                        </div>
                      ))}
                      {dayEvents.length > 3 && (
                        <div style={{ fontSize:'.6rem', color:'var(--text-muted)', paddingLeft:4 }}>+{dayEvents.length-3} mehr</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Event-Detail Popup */}
      {selected && (
        <div style={{ position:'fixed', bottom:80, left:'50%', transform:'translateX(-50%)', zIndex:300, width:'90%', maxWidth:340 }}>
          <div className="card" style={{ boxShadow:'var(--shadow-lg)', border:`2px solid ${selected.color||'#3b82f6'}` }}>
            <div style={{ padding:'12px 14px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:selected.color||'#3b82f6', flexShrink:0 }}/>
                  <span style={{ fontWeight:700, fontSize:'.88rem' }}>{selected.title}</span>
                </div>
                <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setSelected(null)}><X size={14}/></button>
              </div>
              <div style={{ fontSize:'.75rem', color:'var(--text-muted)', marginBottom:4 }}>
                {new Date(selected.start_date).toLocaleDateString('de-DE',{weekday:'long',day:'2-digit',month:'long',year:'numeric'})}
                {selected.license_plate && ` · ${selected.license_plate}`}
              </div>
              {selected.description && <p style={{ fontSize:'.78rem', marginBottom:10 }}>{selected.description}</p>}
              <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                <button className="btn btn-secondary btn-sm" onClick={() => openEdit(selected)}><Edit2 size={12}/> Bearbeiten</button>
                <button className="btn btn-danger btn-sm" onClick={() => del(selected.id)}><Trash2 size={12}/> Löschen</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modal && (
        <div className="overlay" onClick={e => e.target===e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-head">
              <span className="modal-head-title">{editing ? 'Termin bearbeiten' : 'Neuer Termin'}</span>
              <button className="btn btn-ghost btn-icon" onClick={() => setModal(false)}><X size={16}/></button>
            </div>
            <form onSubmit={save} style={{ display:'contents' }}>
              <div className="modal-body">
                <div className="f-group" style={{ marginBottom:12 }}>
                  <label className="f-label">Titel *</label>
                  <input className="f-input" value={form.title} onChange={e => f('title',e.target.value)} required placeholder="Terminbezeichnung..."/>
                </div>

                <div className="form-grid g2" style={{ marginBottom:12 }}>
                  <div className="f-group">
                    <label className="f-label">Typ</label>
                    <select className="f-input" value={form.event_type} onChange={e => f('event_type',e.target.value)}>
                      {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="f-group">
                    <label className="f-label">Fahrzeug</label>
                    <select className="f-input" value={form.vehicle_id} onChange={e => f('vehicle_id',e.target.value)}>
                      <option value="">Keines</option>
                      {vehicles.map(v => <option key={v.id} value={v.id}>{v.license_plate} — {v.brand} {v.model}</option>)}
                    </select>
                  </div>
                </div>

                <div className="form-grid g2" style={{ marginBottom:12 }}>
                  <div className="f-group">
                    <label className="f-label">Startdatum *</label>
                    <input type="date" className="f-input" value={form.start_date} onChange={e => f('start_date',e.target.value)} required/>
                  </div>
                  <div className="f-group">
                    <label className="f-label">Enddatum</label>
                    <input type="date" className="f-input" value={form.end_date} onChange={e => f('end_date',e.target.value)}/>
                  </div>
                </div>

                <div className="f-group" style={{ marginBottom:12 }}>
                  <label className="f-label">Beschreibung</label>
                  <textarea className="f-input" value={form.description} onChange={e => f('description',e.target.value)} rows={3} placeholder="Optionale Details..."/>
                </div>

                <div className="f-group">
                  <label className="f-label">Farbe</label>
                  <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:4 }}>
                    {COLORS.map(c => (
                      <button key={c} type="button"
                        onClick={() => f('color',c)}
                        style={{
                          width:28, height:28, borderRadius:'50%', background:c, border:'none',
                          cursor:'pointer', outline: form.color===c ? `3px solid ${c}` : 'none',
                          outlineOffset:2, transform: form.color===c ? 'scale(1.15)' : 'scale(1)',
                          transition:'all 120ms',
                        }}/>
                    ))}
                    <input type="color" value={form.color} onChange={e => f('color',e.target.value)}
                      style={{ width:28, height:28, border:'none', padding:0, cursor:'pointer', borderRadius:4, background:'none' }}/>
                  </div>
                </div>
              </div>
              <div className="modal-foot">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Abbrechen</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving && <span className="spinner" style={{ width:14,height:14,borderWidth:2 }}/>}
                  {editing ? 'Speichern' : 'Erstellen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
